import { InsurancePackage } from '@app/domain/model/eshop/transport/InsurancePackage';
import { PolicyValidity } from '@app/domain/model/eshop/transport/PolicyValidity';
import { useCallback, useMemo } from 'react';

export const useInsurancePackage = (
    cascoValidity: Nullable<PolicyValidity>,
    mtplValidity: Nullable<PolicyValidity>,
    hasPremiums: boolean = true,
) => {
    const isExpiringWithin30Days = useCallback((validityData: Nullable<PolicyValidity>) => {
        if (!validityData?.validUntil) return false;

        const expirationDate = new Date(validityData.validUntil).getTime();
        const thirtyDaysFromNow = new Date().getTime() + 30 * 24 * 60 * 60 * 1000;

        return expirationDate <= thirtyDaysFromNow;
    }, []);

    const insurancePackage = useMemo(() => {
        if (!hasPremiums) {
            return { status: InsurancePackage.noPremiumsAvailable };
        }

        const isCascoValid = cascoValidity?.isValid ?? false;
        const isMtplValid = mtplValidity?.isValid ?? false;

        const isCascoExpiringSoon = isCascoValid && isExpiringWithin30Days(cascoValidity ?? null);
        const isMtplExpiringSoon = isMtplValid && isExpiringWithin30Days(mtplValidity ?? null);

        if (!isCascoValid && !isMtplValid) {
            return { status: InsurancePackage.cascoMtplInvalid };
        }

        if (!isCascoValid && isMtplValid && !isMtplExpiringSoon) {
            return { status: InsurancePackage.cascoInvalidMtplValidLongTerm };
        }

        if (!isMtplValid && isCascoValid && !isCascoExpiringSoon) {
            return { status: InsurancePackage.mtplInvalidCascoValidLongTerm };
        }

        if (isMtplValid && !isCascoValid && isMtplExpiringSoon) {
            return { status: InsurancePackage.mtplValidExpiringSoonCascoInvalid };
        }

        if (isCascoValid && !isMtplValid && isCascoExpiringSoon) {
            return { status: InsurancePackage.cascoValidExpiringSoonMtplInvalid };
        }

        if (isCascoValid && isMtplValid && isCascoExpiringSoon && !isMtplExpiringSoon) {
            return {
                status: InsurancePackage.bothValidCascoExpiringSoon,
            };
        }

        if (isCascoValid && isMtplValid && !isCascoExpiringSoon && isMtplExpiringSoon) {
            return {
                status: InsurancePackage.bothValidMtplExpiringSoon,
            };
        }

        if (isCascoValid && isMtplValid && !isCascoExpiringSoon && !isMtplExpiringSoon) {
            return {
                status: InsurancePackage.cascoMtplValidLongTerm,
            };
        }

        if (isCascoValid && isMtplValid && isCascoExpiringSoon && isMtplExpiringSoon) {
            return {
                status: InsurancePackage.cascoMtplValidExpiringSoon,
            };
        }

        return {
            status: InsurancePackage.cascoMtplInvalid,
        };
    }, [cascoValidity, mtplValidity, isExpiringWithin30Days, hasPremiums]);

    return insurancePackage;
};
